import { Alert, InputAdornment, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  BenefitDTOBenefitEnum,
  CreateBenefitActivationDTO,
  EmployeeDTOCivilStatusEnum,
  UpdateBenefitActivationDTO,
} from 'probonio-shared-ui/api';
import { apis } from 'probonio-shared-ui/module/api';
import { useTenant } from 'probonio-shared-ui/module/me';
import React, { useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { SelectControl } from '../../../../component/form';
import { BenefitActivationDialogExtensionInterface } from '../../../../extension/interface/benefit-activation-dialog.extension.interface';
import ProfileEmployeeContext from '../../../userManagement/UserProfile/activations/hooks/useProfileEmployee';
import { ActivationDialogExtensions } from '../../ActivationDialog';
import { ActivationContext } from '../../ActivationDialog/BaseActivationDialog';
import { useRecreationMaxBudget } from '../useRecreationMaxBudget';

interface RecreationBenefitOptions {
  civilStatus: EmployeeDTOCivilStatusEnum;
  numberOfKids: number;
}

const Fields: React.FC = () => {
  const { t } = useTranslation('benefitsModule');
  const { tenant } = useTenant();
  const { control, watch } = useFormContext();

  const profileContext = useContext(ProfileEmployeeContext);
  const employee = profileContext?.employee;

  const civilStatus = watch('benefitOptions.civilStatus');
  const numberOfKids = watch('benefitOptions.numberOfKids');

  const maxBudget = useRecreationMaxBudget(civilStatus, numberOfKids);

  return (
    <>
      {employee && (
        <Grid container spacing={1} marginBottom={2}>
          <Grid size={4}>
            <SelectControl
              control={control}
              name="benefitOptions.civilStatus"
              label={t('RECREATION.fields.civilStatus')}
              margin="dense"
              fullWidth
              options={Object.values(EmployeeDTOCivilStatusEnum).map(value => ({ label: t(`common:civilStatus.${value}`), value }))}
              rules={{ required: true }}
            />
          </Grid>
          <Grid size={4}>
            <SelectControl
              control={control}
              name="benefitOptions.numberOfKids"
              label={t('RECREATION.fields.numberOfKids')}
              margin="dense"
              fullWidth
              options={new Array(9).fill(0).map((value, index) => ({ label: `${index}`, value: index }))}
              rules={{ required: true }}
            />
          </Grid>
          <Grid size={4}>
            <TextField
              slotProps={{ input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } }}
              variant="outlined"
              label={t('RECREATION.fields.yearlyBudget')}
              margin="dense"
              fullWidth
              value={maxBudget / 100}
              disabled
            />
          </Grid>
        </Grid>
      )}
      <Alert severity="info">{t(employee ? 'RECREATION.maxBudgetHint' : 'RECREATION.maxBudgetHintMulti', { ...tenant?.taxConfig })}</Alert>
    </>
  );
};

const resetRecreationBenefitOptions: ActivationDialogExtensions<RecreationBenefitOptions>['onResetBenefitOptions'] = (
  selectedActivation,
  context,
): RecreationBenefitOptions => {
  return {
    civilStatus: context?.employee?.civilStatus || EmployeeDTOCivilStatusEnum.Single,
    numberOfKids: context?.employee?.numberOfKids || 0,
  };
};

const mapRecreationOptions = () => ({});

const updateEmployeeIfRequired = async (benefitOptions: RecreationBenefitOptions, context?: ActivationContext) => {
  if (
    context?.employee &&
    (benefitOptions.civilStatus !== context.employee.civilStatus || benefitOptions.numberOfKids !== context.employee.numberOfKids)
  ) {
    await apis.employee.updateEmployee({
      tenantId: context.employee!.tenantId,
      employeeId: context.employee!.id,
      updateEmployeeDTO: {
        civilStatus: benefitOptions.civilStatus,
        numberOfKids: benefitOptions.numberOfKids,
      },
    });
    await context.queryClient.invalidateQueries({ queryKey: ['tenants', context.tenant?.id, ['employees', context.employee?.id]] });
  }
};

const createRecreationBenefitActivation: ActivationDialogExtensions<RecreationBenefitOptions>['onCreateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
  context,
) => {
  const create: CreateBenefitActivationDTO = {
    ...baseValues,
    benefit: BenefitDTOBenefitEnum.Recreation,
    recreationOptions: mapRecreationOptions(),
  };
  await updateEmployeeIfRequired(benefitOptions, context);
  await saveActivation(create);
};

const updateRecreationBenefitActivation: ActivationDialogExtensions<RecreationBenefitOptions>['onUpdateActivation'] = async (
  baseValues,
  benefitOptions,
  saveActivation,
  context,
) => {
  const update: UpdateBenefitActivationDTO = {
    ...baseValues,
    recreationOptions: mapRecreationOptions(),
  };
  await updateEmployeeIfRequired(benefitOptions, context);
  await saveActivation(update);
};

export class RecreationBenefitActivationDialogExtension extends BenefitActivationDialogExtensionInterface<RecreationBenefitOptions> {
  CreateFields?: React.ReactNode = (<Fields />);
  ChangeOptionsFields?: React.ReactNode = (<Fields />);

  onCreateActivation = createRecreationBenefitActivation;
  onUpdateActivation = updateRecreationBenefitActivation;
  onResetBenefitOptions? = resetRecreationBenefitOptions;
}
